// Canonical collection utilities: map types/slugs/synonyms -> canonical slug and display name
// Safe for both server and client use (no Node-only APIs)

function normalize(input: string): string {
  return String(input || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/\/+$/, '')
}

// Define canonical collections and their synonyms
// Keys are canonical slugs; each entry defines a human display name and list of synonyms
const CANONICALS: Record<string, { displayName: string; synonyms: string[] }> = {
  // Base collections (boolean-based)
  'refrigerators': {
    displayName: 'Refrigerators',
    synonyms: ['fridges', 'all-refrigerators', 'all-fridges']
  },
  'freezers': {
    displayName: 'Freezers',
    synonyms: ['all-freezers']
  },
  'specialty-appliances': {
    displayName: 'Specialty Appliances',
    synonyms: ['specialty']
  },
  'energy-star-refrigerators': {
    displayName: 'Energy Star Refrigerators',
    synonyms: []
  },
  'energy-star-freezers': {
    displayName: 'Energy Star Freezers',
    synonyms: []
  },
  'energy-star-specialty-appliances': {
    displayName: 'Energy Star Specialty Appliances',
    synonyms: []
  },
  
  // Refrigerators by type
  'french-door-refrigerators': {
    displayName: 'French Door Refrigerators',
    synonyms: ['french-door', 'french_door']
  },
  'side-by-side-refrigerators': {
    displayName: 'Side-by-Side Refrigerators',
    synonyms: ['side-by-side', 'side_by_side', 'sxs']
  },
  'top-freezer-refrigerators': {
    displayName: 'Top Freezer Refrigerators',
    synonyms: ['top-freezer', 'top_freezer']
  },
  'bottom-freezer-refrigerators': {
    displayName: 'Bottom Freezer Refrigerators',
    synonyms: ['bottom-freezer', 'bottom_freezer']
  },
  'mini-fridges': {
    displayName: 'Mini Fridges',
    // FRIDGE-1460: compact-refrigerators retired into this page; its synonyms resolve here.
    synonyms: ['mini-fridge', 'mini_fridge', 'compact-refrigerators', 'compact-refrigerator']
  },
  'compact-mini-fridges': {
    displayName: 'Compact & Mini Fridges',
    synonyms: ['compact-mini-fridge']
  },
  'office-refrigerators': {
    displayName: 'Office Refrigerators',
    synonyms: []
  },
  'apartment-refrigerators': {
    displayName: 'Apartment Refrigerators',
    synonyms: ['apartment-size-refrigerators']
  },
  'garage-refrigerators': {
    displayName: 'Garage Refrigerators',
    synonyms: ['garage-ready-refrigerators']
  },
  'quiet-refrigerators': {
    displayName: 'Quiet Refrigerators',
    synonyms: ['silent-refrigerators', 'low-noise-refrigerators']
  },
  'freezerless-refrigerators': {
    displayName: 'Freezerless Refrigerators',
    synonyms: []
  },
  'energy-efficient-refrigerators': {
    displayName: 'Energy Efficient Refrigerators',
    synonyms: ['energy-star-refrigerators']
  },
  'energy-efficient-freezers': {
    displayName: 'Energy Efficient Freezers',
    synonyms: ['energy-star-freezers']
  },
  'retro-fridges': {
    displayName: 'Retro Fridges',
    synonyms: ['retro-refrigerators']
  },
  'dorm-refrigerators': {
    displayName: 'Dorm Refrigerators',
    synonyms: []
  },
  'undercounter-refrigerators': {
    displayName: 'Undercounter Refrigerators',
    synonyms: []
  },
  'built-in-refrigerators': {
    displayName: 'Built-In Refrigerators',
    synonyms: []
  },
  'portable-refrigerators': {
    displayName: 'Portable Refrigerators',
    synonyms: []
  },
  'kimchi-refrigerators': {
    displayName: 'Kimchi Refrigerators',
    synonyms: []
  },
  // Freezers
  'upright-freezers': {
    displayName: 'Upright Freezers',
    synonyms: ['upright-freezer', 'upright_freezer']
  },
  'chest-freezers': {
    displayName: 'Chest Freezers',
    synonyms: ['chest-freezer', 'chest_freezer']
  },
  'compact-freezers': {
    displayName: 'Compact Freezers',
    synonyms: ['compact-freezer', 'compact_freezer']
  },
  'mini-freezers': {
    displayName: 'Mini Freezers',
    synonyms: []
  },
  'office-freezers': {
    displayName: 'Office Freezers',
    synonyms: []
  },
  'deep-freezers': {
    displayName: 'Deep Freezers',
    synonyms: []
  },
  'small-chest-freezers': {
    displayName: 'Small Chest Freezers',
    synonyms: []
  },
  'undercounter-freezers': {
    displayName: 'Undercounter Freezers',
    synonyms: []
  },
  'drawer-freezers': {
    displayName: 'Drawer Freezers',
    synonyms: ['drawer-freezer']
  },
  'double-drawer-freezers': {
    displayName: 'Double Drawer Freezers',
    synonyms: ['double-drawer-freezer', 'two-drawer-freezers', 'dual-drawer-freezers']
  },
  'outdoor-freezers': {
    displayName: 'Outdoor Freezers',
    synonyms: []
  },
  'freezer-coolers': {
    displayName: 'Freezer Coolers',
    synonyms: ['convertible-freezers']
  },
  'garage-freezers': {
    displayName: 'Garage Freezers',
    synonyms: ['garage-ready-freezers']
  },
  'built-in-freezers': {
    displayName: 'Built-In Freezers',
    synonyms: []
  },
  // Specialty
  'wine-coolers': {
    displayName: 'Wine Coolers',
    synonyms: ['wine-cooler', 'wine_cooler']
  },
  'beverage-coolers': {
    displayName: 'Beverage Coolers',
    synonyms: ['beverage-cooler', 'beverage_cooler', 'beverage-center', 'beverage_center']
  },
  'kegerators': {
    displayName: 'Kegerators',
    synonyms: ['kegerator']
  },
  'ice-makers': {
    displayName: 'Ice Makers',
    synonyms: ['ice-maker', 'ice_maker']
  },
  'outdoor-refrigerators': {
    displayName: 'Outdoor Refrigerators',
    synonyms: ['outdoor-fridge', 'outdoor_fridge', 'outdoor-refrigerator']
  },
  'beer-fridges': {
    displayName: 'Beer Fridges',
    synonyms: ['beer-fridge']
  },
  'wine-fridges': {
    displayName: 'Wine Fridges',
    synonyms: ['wine-fridge']
  },
  'smart-fridges': {
    displayName: 'Smart Fridges',
    synonyms: ['smart-fridge', 'smart-refrigerators', 'smart-refrigerator', 'wifi-refrigerators', 'connected-refrigerators']
  },
  'garage-ready-refrigerators': {
    displayName: 'Garage Ready Refrigerators',
    synonyms: ['garage-ready-fridges']
  },
  'beverage-fridges': {
    displayName: 'Beverage Fridges',
    synonyms: ['beverage-fridge']
  },
  'beverage-centers': {
    displayName: 'Beverage Centers',
    synonyms: ['beverage-center']
  },
  // FRIDGE-651 Phase 3 (10/21/2026): the water-coolers node. The decision of
  // 10/17/2026 (on the card, beside lib/product-classifier/vocabularies.ts):
  // a powered water cooler that refrigerates water carries a FIN, its node is
  // /collections/water-coolers, and water-dispensers is its synonym. The old
  // water-dispensers head is retired into it: /collections/water-dispensers
  // and its 123 indexed use-case tiers 308 here in one hop
  // (lib/manual-redirects.csv), because a synonym is not a key and
  // parseCollectionUrl matches keys only, so the retired head's composites
  // parse to nothing. The type value and the retired slug are the synonyms;
  // the feature word "water dispenser" (a refrigerator's own water dispenser)
  // deliberately is not, so inferCanonicalSlugFromText never files a
  // refrigerator here.
  'water-coolers': {
    displayName: 'Water Coolers',
    synonyms: ['water-cooler', 'water_cooler', 'water-dispensers']
  },
  // The one child above the three-product floor on 10/21/2026 (3 of the 8 live
  // rows say hot and cold). bottleless, bottom-loading, top-loading, countertop
  // and self-cleaning held 1, 2, 2, 0 and 0 and stay unregistered until they
  // clear the floor (tests/water-coolers-node-fridge-651.test.ts).
  'hot-and-cold-water-coolers': {
    displayName: 'Hot and Cold Water Coolers',
    synonyms: ['hot-and-cold-water-cooler', 'hot-cold-water-coolers']
  },
  // Feature/tag-based
  'counter-depth-refrigerators': {
    displayName: 'Counter-Depth Refrigerators',
    synonyms: ['counter-depth', 'counter_depth']
  },
  'door-in-door-refrigerators': {
    displayName: 'Door-in-Door Refrigerators',
    synonyms: ['door-in-door', 'door_in_door']
  },
  '4-door-refrigerators': {
    displayName: '4-Door Refrigerators',
    synonyms: ['four-door-refrigerators', '4-door', 'four-door', 'four_door']
  },
  'fingerprint-resistant-refrigerators': {
    displayName: 'Fingerprint Resistant Refrigerators',
    synonyms: ['fingerprint-resistant', 'fingerprint_resistant']
  },
  'black-stainless-refrigerators': {
    displayName: 'Black Stainless Refrigerators',
    synonyms: ['black-stainless', 'black_stainless']
  },
  'stainless-steel-refrigerators': {
    displayName: 'Stainless Steel Refrigerators',
    synonyms: ['stainless-steel', 'stainless']
  },
  // Widths
  '36-inch-wide-refrigerators': {
    displayName: '36-Inch Wide Refrigerators',
    synonyms: ['36-inch', '36-inch-wide', '36in']
  },
  '30-inch-wide-refrigerators': {
    displayName: '30-Inch Wide Refrigerators',
    synonyms: ['30-inch', '30-inch-wide', '30in']
  },
  '24-inch-wide-refrigerators': {
    displayName: '24-Inch Wide Refrigerators',
    synonyms: ['24-inch', '24-inch-wide', '24in']
  },
  '28-inch-wide-refrigerators': {
    displayName: '28-Inch Wide Refrigerators',
    synonyms: ['28-inch', '28-inch-wide', '28in']
  },
  '33-inch-wide-refrigerators': {
    displayName: '33-Inch Wide Refrigerators',
    synonyms: ['33-inch', '33-inch-wide', '33in']
  },
  '18-inch-wide-refrigerators': {
    displayName: '18-Inch Wide Refrigerators',
    synonyms: ['18-inch', '18-inch-wide', '18in']
  },
  '42-inch-wide-refrigerators': {
    displayName: '42-Inch Wide Refrigerators',
    synonyms: ['42-inch', '42-inch-wide', '42in']
  },
  '48-inch-wide-refrigerators': {
    displayName: '48-Inch Wide Refrigerators',
    synonyms: ['48-inch', '48-inch-wide', '48in']
  },
  // FRIDGE-44 filter-hub lane: the remaining WIDTH_RANGES buckets, registered
  // so each slug resolves to ITSELF (parser step 5 exact match) and the
  // width_in query in lib/collection-page-data.ts applies. Whether a width hub
  // actually gets a URL is governed by the FILTER_HUB_FLOOR depth gate in
  // lib/filter-hubs.ts — registration alone never ships a thin page.
  '20-inch-wide-refrigerators': {
    displayName: '20-Inch Wide Refrigerators',
    synonyms: ['20-inch', '20-inch-wide', '20in']
  },
  '21-inch-wide-refrigerators': {
    displayName: '21-Inch Wide Refrigerators',
    synonyms: ['21-inch', '21-inch-wide', '21in']
  },
  '22-inch-wide-refrigerators': {
    displayName: '22-Inch Wide Refrigerators',
    synonyms: ['22-inch', '22-inch-wide', '22in']
  },
  '23-inch-wide-refrigerators': {
    displayName: '23-Inch Wide Refrigerators',
    synonyms: ['23-inch', '23-inch-wide', '23in']
  },
  '26-inch-wide-refrigerators': {
    displayName: '26-Inch Wide Refrigerators',
    synonyms: ['26-inch', '26-inch-wide', '26in']
  },
  '27-inch-wide-refrigerators': {
    displayName: '27-Inch Wide Refrigerators',
    synonyms: ['27-inch', '27-inch-wide', '27in']
  },
  '29-inch-wide-refrigerators': {
    displayName: '29-Inch Wide Refrigerators',
    synonyms: ['29-inch', '29-inch-wide', '29in']
  },
  '31-inch-wide-refrigerators': {
    displayName: '31-Inch Wide Refrigerators',
    synonyms: ['31-inch', '31-inch-wide', '31in']
  },
  '32-inch-wide-refrigerators': {
    displayName: '32-Inch Wide Refrigerators',
    synonyms: ['32-inch', '32-inch-wide', '32in']
  },
  '34-inch-wide-refrigerators': {
    displayName: '34-Inch Wide Refrigerators',
    synonyms: ['34-inch', '34-inch-wide', '34in']
  },
  '35-inch-wide-refrigerators': {
    displayName: '35-Inch Wide Refrigerators',
    synonyms: ['35-inch', '35-inch-wide', '35in']
  },
  // FRIDGE-44 filter-hub lane: bottle-capacity hubs over the wine-cooler
  // family (normalized products.bottle_capacity, FRIDGE-33). Bucket boundaries
  // mirror the sidebar bottle facet. Same floor-gate rule as the width hubs.
  'under-20-bottle-wine-coolers': {
    displayName: 'Under 20 Bottle Wine Coolers',
    synonyms: ['under-20-bottle', 'under-20-bottles']
  },
  '20-to-40-bottle-wine-coolers': {
    displayName: '20 to 40 Bottle Wine Coolers',
    synonyms: ['20-to-40-bottle', '20-40-bottle']
  },
  '40-to-60-bottle-wine-coolers': {
    displayName: '40 to 60 Bottle Wine Coolers',
    synonyms: ['40-to-60-bottle', '40-60-bottle']
  },
  '60-to-100-bottle-wine-coolers': {
    displayName: '60 to 100 Bottle Wine Coolers',
    synonyms: ['60-to-100-bottle', '60-100-bottle']
  },
  '100-bottle-and-up-wine-coolers': {
    displayName: '100 Bottle and Up Wine Coolers',
    synonyms: ['100-bottle-and-up', '100-plus-bottle']
  },
  // Capacities - Format: "X to Y Cu Ft Refrigerators"
  'under-20-cu-ft-refrigerators': {
    displayName: 'Under 20 Cu Ft Refrigerators',
    synonyms: ['under-20-cu-ft', 'capacity_under_20']
  },
  '20-to-24-cu-ft-refrigerators': {
    displayName: '20 to 24 Cu Ft Refrigerators',
    synonyms: ['20-to-24-cu-ft', 'capacity_20_24']
  },
  '24-to-28-cu-ft-refrigerators': {
    displayName: '24 to 28 Cu Ft Refrigerators',
    synonyms: ['24-to-28-cu-ft', 'capacity_24_28']
  },
  '28-cu-ft-and-up-refrigerators': {
    displayName: '28 Cu Ft and Up Refrigerators',
    synonyms: ['28-cu-ft-and-up', 'capacity_28_plus']
  },
  // Commercial Collections
  'commercial-refrigerators-and-freezers': {
    displayName: 'Commercial Refrigerators & Freezers',
    synonyms: ['commercial', 'commercial-products']
  },
  'commercial-refrigerators': {
    displayName: 'Commercial Refrigerators',
    synonyms: ['commercial-refrigerator', 'commercial-fridge', 'commercial-fridges']
  },
  'commercial-freezers': {
    displayName: 'Commercial Freezers',
    synonyms: ['commercial-freezer']
  },
  'commercial-bar-coolers': {
    displayName: 'Commercial Bar Coolers',
    synonyms: ['commercial-bar-cooler']
  },
  'back-bar-coolers': {
    displayName: 'Back Bar Coolers',
    synonyms: ['back-bar-cooler', 'bar-cooler', 'bar-coolers']
  },
  'commercial-display-cases': {
    displayName: 'Commercial Display Cases',
    synonyms: ['commercial-display', 'display-case', 'display-cases', 'merchandiser', 'merchandisers']
  },
  // Note: display-cases should use the canonical commercial-display-cases URL
  'commercial-undercounter': {
    displayName: 'Commercial Undercounter',
    synonyms: ['commercial-under-counter', 'undercounter-commercial']
  },
  'commercial-beverage-coolers': {
    displayName: 'Commercial Beverage Coolers',
    synonyms: ['commercial-beverage-cooler']
  },
  'commercial-kegerators': {
    displayName: 'Commercial Kegerators',
    synonyms: ['commercial-kegerator']
  },
  'commercial-undercounter-refrigerators': {
    displayName: 'Commercial Undercounter Refrigerators',
    synonyms: ['commercial-undercounter-refrigerator']
  },
  // FRIDGE-619 — slugs that were CONFIGURED in lib/collection-filters.ts
  // NOTE: these entries carry NO synonyms on purpose — the parser only needs
  // the canonical KEY, and a synonym here feeds inferCanonicalSlugFromText,
  // which builds PDP breadcrumbs from title text: a 'column-freezer' synonym
  // pointed breadcrumbs at the gated 1-member column-freezers page
  // (tests/breadcrumbs-helpers.test.ts caught it).
  // (TYPE_TO_DB/TITLE_SEARCH) but never registered here, so parser step 5
  // stripped a canonical SUFFIX and treated the remainder as a phantom BRAND:
  // "drawer-refrigerators" served refrigerators by brand "Drawer" — zero of
  // the 29 real drawer units — and every slug below measured 0 (or wrong-
  // brand) members the same way, each then auto-gated for looking empty.
  // Registration makes the full slug win the exact match, which is the same
  // cure the REPLACEMENT FILTERS block below and FRIDGE-44's width hubs
  // already document. Registration alone publishes nothing: the nightly
  // count + FRIDGE-522 drain govern visibility through normal thresholds.
  // Deliberately NOT registered: 4-door-flex-refrigerators (FRIDGE-561
  // TYPE_BRIDGE routes the type onto 4-door-refrigerators) and dorm-fridges,
  // built-in-ice-makers, built-in-wine-coolers, single-zone-wine-coolers
  // (gated zero_signal_2y by OWNER RULE 07/09/2026 — wiring their membership
  // would hand the publish decision to the mechanical drain). Pinned by
  // tests/collection-registry-parse-identity.test.ts.
  'drawer-refrigerators': {
    displayName: 'Drawer Refrigerators',
    synonyms: []
  },
  'single-door-refrigerators': {
    displayName: 'Single Door Refrigerators',
    synonyms: []
  },
  'double-drawer-refrigerators': {
    displayName: 'Double Drawer Refrigerators',
    synonyms: []
  },
  'column-refrigerators': {
    displayName: 'Column Refrigerators',
    synonyms: []
  },
  'column-freezers': {
    displayName: 'Column Freezers',
    synonyms: []
  },
  'portable-freezers': {
    displayName: 'Portable Freezers',
    synonyms: []
  },
  'quiet-freezers': {
    displayName: 'Quiet Freezers',
    synonyms: []
  },
  'retro-freezers': {
    displayName: 'Retro Freezers',
    synonyms: []
  },
  'humidity-control-refrigerators': {
    displayName: 'Humidity Control Refrigerators',
    synonyms: []
  },
  'gaming-fridges': {
    displayName: 'Gaming Fridges',
    // FRIDGE-1461: gaming-mini-fridges retired into this page; its slug resolves here.
    synonyms: ['gaming-mini-fridges', 'gaming-mini-fridge']
  },
  'dual-zone-wine-coolers': {
    displayName: 'Dual Zone Wine Coolers',
    synonyms: []
  },
  // water-coolers was deliberately unregistered from 08/29/2026 (2 active
  // units, FRIDGE-619) until FRIDGE-651 Phase 3 registered it above with 8
  // live rows (10/21/2026); the retired water-dispensers entry became its
  // synonym and its indexed tiers got their redirect rows in the same release.
  'commercial-undercounter-freezers': {
    displayName: 'Commercial Undercounter Freezers',
    synonyms: []
  },
  // === REPLACEMENT FILTERS ===
  // Without these, parseCollectionUrl treats slugs like "water-filters" as
  // brand names and the products query collapses to zero. Registering them
  // here lets the parser set typeSlug correctly.
  'water-filters': {
    displayName: 'Water Filters',
    synonyms: ['water-filter', 'refrigerator-water-filter', 'refrigerator-water-filters']
  },
  'air-filters': {
    displayName: 'Air Filters',
    synonyms: ['air-filter', 'refrigerator-air-filter', 'refrigerator-air-filters']
  },
  'refrigerator-filters': {
    displayName: 'Refrigerator Filters',
    synonyms: ['refrigerator-filter', 'fridge-filters', 'fridge-filter']
  },
  'condenser-filters': {
    displayName: 'Condenser Filters',
    synonyms: ['condenser-filter']
  },
  'ice-machine-filters': {
    displayName: 'Ice Machine Filters',
    synonyms: ['ice-machine-filter', 'ice-maker-filter', 'ice-maker-filters']
  },
  'inline-filters': {
    displayName: 'Inline Filters',
    synonyms: ['inline-filter', 'in-line-filter', 'in-line-filters']
  },
}

// Build lookup maps (normalized)
const synonymToCanonical = new Map<string, string>()
const canonicalToDisplay = new Map<string, string>()

Object.entries(CANONICALS).forEach(([canonical, { displayName, synonyms }]) => {
  const normCanon = normalize(canonical)
  canonicalToDisplay.set(normCanon, displayName)
  synonymToCanonical.set(normCanon, normCanon)
  synonyms.forEach(s => synonymToCanonical.set(normalize(s), normCanon))
})

export function canonicalSlugForTypeOrSlug(input: string | undefined | null): string | null {
  if (!input) return null
  const norm = normalize(input)
  return synonymToCanonical.get(norm) || null
}

export function displayNameForCollectionSlugOrType(input: string | undefined | null): string | null {
  if (!input) return null
  const canon = canonicalSlugForTypeOrSlug(input)
  if (canon) return canonicalToDisplay.get(canon) || null
  return null
}

export function ensureCanonicalSlug(input: string): { canonicalSlug: string; changed: boolean } {
  const canon = canonicalSlugForTypeOrSlug(input)
  if (canon) return { canonicalSlug: canon, changed: normalize(input) !== canon }
  return { canonicalSlug: normalize(input), changed: false }
}

/**
 * Format product type for display (singular form)
 * Converts database type (e.g., "french_door") to display name (e.g., "French Door Refrigerator")
 * Used in comparison tables and product specs
 */
export function formatProductType(type: string | undefined | null): string {
  if (!type) return 'N/A'

  // Get the display name from canonical mapping
  const displayName = displayNameForCollectionSlugOrType(type)

  if (displayName) {
    // Convert plural to singular: "French Door Refrigerators" → "French Door Refrigerator"
    return displayName.replace(/s$/, '')
  }

  // Fallback: format the raw type string
  // Replace underscores and hyphens with spaces, then title case
  const formatted = type
    .replace(/[_-]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

  return formatted
}

export function allCanonicalSlugs(): string[] {
  return Array.from(canonicalToDisplay.keys())
}

export function isCanonicalCollection(slug: string): boolean {
  return canonicalToDisplay.has(normalize(slug))
}

export function displayNameForCanonicalSlug(canonicalSlug: string): string | null {
  return canonicalToDisplay.get(normalize(canonicalSlug)) || null
}

// Infer a canonical collection slug from arbitrary free text (e.g., product title)
// Scans the normalized text for any known synonym substrings and returns the first match
export function inferCanonicalSlugFromText(text: string | undefined | null): string | null {
  if (!text) return null
  const normText = normalize(text)
  for (const [synonym, canon] of synonymToCanonical.entries()) {
    if (normText.includes(synonym)) {
      return canon
    }
  }
  return null
}


